import React, { useState, useEffect } from 'react';

const API = 'https://n293vk0zm6.execute-api.us-east-1.amazonaws.com/prod';

const PIPELINE_STEPS = [
  {
    number: '01',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
      </svg>
    ),
    title: 'Daily scheduled fetch',
    description: 'Every morning at 8am ET, EventBridge triggers the ingest Lambda. It calls the Last.fm API and pulls the Global Top 50 tracks — rank, playcount, and listener counts for each track.',
    tag: 'EventBridge → Lambda → Last.fm',
  },
  {
    number: '02',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <ellipse cx="12" cy="5" rx="9" ry="3" />
        <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
        <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
      </svg>
    ),
    title: 'Snapshot stored in S3',
    description: "Each day's Top 50 is saved as a timestamped JSON snapshot in S3. These snapshots are the historical record the ML models train on — the longer the project runs, the richer the signal.",
    tag: 'S3 snapshots',
  },
  {
    number: '03',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
      </svg>
    ),
    title: 'ML anomaly detection',
    description: 'At 9am ET, one hour after ingest, the ML Lambda runs two models — Isolation Forest and K-Means clustering — across all tracks with 5+ days of history. Tracks behaving unusually compared to their own baseline get flagged and written to DynamoDB.',
    tag: 'ML Lambda → DynamoDB',
  },
  {
    number: '04',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="7" height="7" />
        <rect x="14" y="3" width="7" height="7" />
        <rect x="14" y="14" width="7" height="7" />
        <rect x="3" y="14" width="7" height="7" />
      </svg>
    ),
    title: 'Results served to dashboard',
    description: 'Alerts are stored in DynamoDB and served via API Gateway. This dashboard reads live from those endpoints — every alert, score, and prediction you see here is real data from the pipeline, updated every morning.',
    tag: 'API Gateway → Dashboard',
  },
];

const MODEL_CARDS = [
  {
    name: 'Isolation Forest',
    shortName: 'IF',
    color: 'var(--accent)',
    bgColor: 'var(--accent-bg)',
    borderColor: 'var(--accent-bd)',
    projectedPrecision: 58,
    modelKey: 'isolation_forest',
    description: "Looks at each track in isolation and asks: does this track's behavior today look strange compared to how it normally behaves? It flags tracks with unusually large jumps in playcount or listener growth.",
    detail: 'Good at catching solo outliers — tracks that spike suddenly without any correlated movement across the chart. Implemented directly in NumPy without scikit-learn due to AWS Lambda\'s 250MB layer size limit — mathematically identical to the standard algorithm.',
  },
  {
    name: 'K-Means Clustering',
    shortName: 'KM',
    color: 'var(--purple)',
    bgColor: 'var(--purple-bg)',
    borderColor: 'var(--purple-bd)',
    projectedPrecision: 45,
    modelKey: 'kmeans',
    description: 'Groups all 50 tracks into 4 clusters based on engagement patterns (playcount vs. listeners). A track that shifts from one cluster to another overnight gets flagged as a cluster-shift anomaly.',
    detail: 'Good at catching relative movers — tracks climbing into a higher engagement neighborhood, which often precedes a chart climb. v2 will upgrade to a 30-day stable centroid baseline for stronger, more reliable shift detection.',
  },
];

function PrecisionBar({ value, color, label, isReal }) {
  return (
    <div style={{ marginBottom: '12px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
        <span style={{ fontSize: '13px', color: 'var(--sub)', fontWeight: 500 }}>{label}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ fontSize: '13px', fontWeight: 600, color }}>{value}%</span>
          <span style={{ fontSize: '10px', background: isReal ? 'var(--accent-bg)' : 'var(--amber-bg)', color: isReal ? 'var(--accent)' : 'var(--amber-dark)', border: `1px solid ${isReal ? 'var(--accent-bd)' : 'var(--amber-bd)'}`, borderRadius: '4px', padding: '1px 5px', fontWeight: 500 }}>
            {isReal ? 'Real data' : 'Projected'}
          </span>
        </div>
      </div>
      <div style={{ height: '8px', background: 'var(--track-bg)', borderRadius: '4px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${value}%`, background: color, borderRadius: '4px' }} />
      </div>
    </div>
  );
}

export default function HowItWorks() {
  const [expandedModel, setExpandedModel] = useState(null);
  const [precision, setPrecision]         = useState(null); // null = loading/no data

  useEffect(() => {
    fetch(`${API}/alerts?all=true`)
      .then(r => r.json())
      .then(data => {
        const rows   = data.alerts || [];
        const closed = rows.filter(r => r.outcome && r.outcome !== 'pending');

        const precisionFor = (modelKey) => {
          const subset = modelKey === 'all'
            ? closed
            : closed.filter(r => r.models_fired === modelKey);
          if (!subset.length) return { pct: null, n: 0 };
          const hits = subset.filter(r =>
            r.outcome === 'went_viral' || r.outcome === 'partial_rise'
          ).length;
          return { pct: Math.round(hits / subset.length * 100), n: subset.length };
        };

        setPrecision({
          both: precisionFor('isolation_forest+kmeans'),
          if:   precisionFor('isolation_forest'),
          km:   precisionFor('kmeans'),
        });
      })
      .catch(() => setPrecision(null));
  }, []);

  // Helper: return real pct if available, else projected target
  const getPrec = (key, projected) => {
    if (!precision) return { value: projected, isReal: false };
    const p = precision[key];
    return p.n > 0 ? { value: p.pct, isReal: true } : { value: projected, isReal: false };
  };

  const bothPrec = getPrec('both', 71);
  const ifPrec   = getPrec('if',   58);
  const kmPrec   = getPrec('km',   45);

  return (
    <div style={{ padding: '8px 0 32px' }}>

      {/* Pipeline Section */}
      <div style={{ marginBottom: '40px' }}>
        <div style={{ marginBottom: '20px' }}>
          <h2 style={{ fontSize: '16px', fontWeight: 700, color: 'var(--text)', margin: '0 0 4px' }}>Pipeline overview</h2>
          <p style={{ fontSize: '13px', color: 'var(--muted)', margin: 0 }}>
            Four steps from raw chart data to anomaly alerts — fully automated, runs every day.
          </p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {PIPELINE_STEPS.map((step, idx) => (
            <div key={step.number} style={{ display: 'flex', alignItems: 'stretch' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '48px', flexShrink: 0 }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'var(--accent-bg)', border: '2px solid var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent)', fontSize: '12px', fontWeight: 700, flexShrink: 0, zIndex: 1 }}>
                  {step.number}
                </div>
                {idx < PIPELINE_STEPS.length - 1 && (
                  <div style={{ width: '2px', flex: 1, background: 'var(--border)', margin: '4px 0' }} />
                )}
              </div>

              <div style={{ flex: 1, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '10px', padding: '16px', marginLeft: '12px', marginBottom: idx < PIPELINE_STEPS.length - 1 ? '12px' : '0' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                  <div style={{ color: 'var(--accent)', marginTop: '1px', flexShrink: 0 }}>{step.icon}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px', marginBottom: '6px' }}>
                      <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text)' }}>{step.title}</span>
                      <span style={{ fontSize: '11px', background: 'var(--track-bg)', color: 'var(--muted)', border: '1px solid var(--border)', borderRadius: '4px', padding: '1px 6px', fontFamily: 'monospace' }}>
                        {step.tag}
                      </span>
                    </div>
                    <p style={{ fontSize: '13px', color: 'var(--sub)', margin: 0, lineHeight: '1.6' }}>{step.description}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Models Section */}
      <div style={{ marginBottom: '32px' }}>
        <div style={{ marginBottom: '20px' }}>
          <h2 style={{ fontSize: '16px', fontWeight: 700, color: 'var(--text)', margin: '0 0 4px' }}>Detection models</h2>
          <p style={{ fontSize: '13px', color: 'var(--muted)', margin: 0 }}>
            Two models, two different lenses. Each catches anomalies the other might miss.
          </p>
        </div>

        <div className="hiw-model-grid">
          {MODEL_CARDS.map((model) => {
            const prec = model.modelKey === 'isolation_forest' ? ifPrec : kmPrec;
            return (
              <div
                key={model.name}
                onClick={() => setExpandedModel(expandedModel === model.name ? null : model.name)}
                style={{ background: expandedModel === model.name ? model.bgColor : 'var(--surface)', border: `1px solid ${expandedModel === model.name ? model.borderColor : 'var(--border)'}`, borderRadius: '10px', padding: '16px', cursor: 'pointer', transition: 'all 0.2s ease' }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                  <div>
                    <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: model.bgColor, border: `1px solid ${model.borderColor}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 700, color: model.color, marginBottom: '8px', fontFamily: 'monospace' }}>
                      {model.shortName}
                    </div>
                    <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text)' }}>{model.name}</span>
                  </div>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--neutral)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                    style={{ transform: expandedModel === model.name ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease', marginTop: '4px' }}>
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </div>

                <p style={{ fontSize: '13px', color: 'var(--sub)', margin: '0 0 12px', lineHeight: '1.6' }}>{model.description}</p>

                {expandedModel === model.name && (
                  <div style={{ background: 'var(--surface)', border: `1px solid ${model.borderColor}`, borderRadius: '6px', padding: '10px 12px', marginBottom: '12px', fontSize: '12px', color: 'var(--sub)', lineHeight: '1.6' }}>
                    {model.detail}
                  </div>
                )}

                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                    <span style={{ fontSize: '12px', color: 'var(--muted)' }}>Standalone precision</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <span style={{ fontSize: '12px', fontWeight: 600, color: model.color }}>{prec.value}%</span>
                      <span style={{ fontSize: '10px', background: prec.isReal ? 'var(--accent-bg)' : 'var(--amber-bg)', color: prec.isReal ? 'var(--accent)' : 'var(--amber-dark)', border: `1px solid ${prec.isReal ? 'var(--accent-bd)' : 'var(--amber-bd)'}`, borderRadius: '3px', padding: '0 4px' }}>
                        {prec.isReal ? 'Real data' : 'Projected'}
                      </span>
                    </div>
                  </div>
                  <div style={{ height: '6px', background: 'var(--track-bg)', borderRadius: '3px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${prec.value}%`, background: model.color, borderRadius: '3px' }} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Dual-model card */}
        <div style={{ background: 'var(--amber-bg)', border: '1px solid var(--amber-bd)', borderRadius: '10px', padding: '16px', marginTop: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: 'var(--amber-bg)', border: '1px solid var(--amber-bd)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--amber)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
              </svg>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px', marginBottom: '6px' }}>
                <span style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text)' }}>Dual-model agreement</span>
                <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--amber)', background: 'var(--amber-bg)', border: '1px solid var(--amber-bd)', borderRadius: '4px', padding: '1px 6px' }}>{bothPrec.value}% precision</span>
                <span style={{ fontSize: '10px', background: bothPrec.isReal ? 'var(--accent-bg)' : 'var(--amber-bg)', color: bothPrec.isReal ? 'var(--accent)' : 'var(--amber-dark)', border: `1px solid ${bothPrec.isReal ? 'var(--accent-bd)' : 'var(--amber-bd)'}`, borderRadius: '4px', padding: '1px 5px' }}>
                  {bothPrec.isReal ? 'Real data' : 'Projected'}
                </span>
              </div>
              <p style={{ fontSize: '13px', color: 'var(--sub)', margin: 0, lineHeight: '1.6' }}>
                When both models independently flag the same track on the same day, confidence is highest. The two models catch anomalies through different lenses — when they agree, it's a stronger signal.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Precision Summary */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '10px', padding: '20px', marginBottom: '20px' }}>
        <div style={{ marginBottom: '16px' }}>
          <h2 style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text)', margin: '0 0 4px' }}>Precision comparison</h2>
          <p style={{ fontSize: '12px', color: 'var(--muted)', margin: 0 }}>
            Precision measures how often a flagged track climbed 5% or more of its remaining rank positions within 7 days. Figures update automatically as outcome windows close.
          </p>
        </div>
        <PrecisionBar value={bothPrec.value} color="var(--amber)"  label="Both models agree (dual-model)" isReal={bothPrec.isReal} />
        <PrecisionBar value={ifPrec.value}   color="var(--accent)" label="Isolation Forest only"          isReal={ifPrec.isReal}   />
        <PrecisionBar value={kmPrec.value}   color="var(--purple)" label="K-Means only"                   isReal={kmPrec.isReal}   />
        <p style={{ fontSize: '12px', color: 'var(--neutral)', margin: '12px 0 0', lineHeight: '1.5' }}>
          {bothPrec.isReal || ifPrec.isReal || kmPrec.isReal
            ? 'Precision figures based on closed 7-day outcome windows.'
            : 'Projected targets based on dual-model architecture. Real precision figures will replace these as 7-day outcome windows close for flagged tracks.'
          }
        </p>
      </div>

      {/* Data source + v2 note */}
      <div style={{ background: 'var(--info-bg)', border: '1px solid var(--border)', borderRadius: '10px', padding: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
          <div style={{ flexShrink: 0, marginTop: '2px' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--sub)', marginBottom: '6px' }}>
              About the data source
            </div>
            <p style={{ fontSize: '12px', color: 'var(--muted)', margin: '0 0 8px', lineHeight: '1.6' }}>
              ChartPulse v1 uses Last.fm chart data — a music scrobbling platform with a dedicated but smaller audience than mainstream streaming platforms. Predictions reflect momentum within the Last.fm ecosystem and may not fully capture cross-platform virality.
            </p>
            <p style={{ fontSize: '12px', color: 'var(--muted)', margin: 0, lineHeight: '1.6' }}>
              <span style={{ color: 'var(--accent)', fontWeight: 500 }}>v2 upgrade path:</span> Migrating to YouTube Data API (viewCount, likeCount, commentCount) for broader cross-platform signal — same AWS infrastructure and ML architecture, stronger data source.
            </p>
          </div>
        </div>
      </div>

    </div>
  );
}